import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useState, type ReactNode } from 'react'
import { SECTOR_BASE, SECTOR_CONTENT, type SectorId } from '../data/sectors'
import { orgSchema, breadcrumbSchema } from '../lib/seo'
import SeoHead from '../components/SeoHead'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

// ───────────── Animated icons (one variant per sector) ─────────────

const ICON_VARIANTS = {
  rest: { y: 0, rotate: 0, scale: 1 },
  hover: { y: 0, rotate: 0, scale: 1 },
}

function ModeIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial="rest"
      animate={hover ? 'hover' : 'rest'}
      variants={ICON_VARIANTS}
    >
      {/* hanger top */}
      <motion.path
        d="M12 4l-2 2c0 1 1 2 2 2s2-1 2-2L12 4z"
        variants={{ rest: { y: 0 }, hover: { y: -1 } }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      />
      {/* hanger swing */}
      <motion.g
        variants={{ rest: { rotate: 0 }, hover: { rotate: [-3, 3, -2, 2, 0] } }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: '12px 8px' }}
      >
        <path d="M12 8L4 14l3 1 5-3 5 3 3-1-8-6z" />
        <path d="M7 15v4h10v-4" />
      </motion.g>
    </motion.svg>
  )
}

function CosmetiqueIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial="rest"
      animate={hover ? 'hover' : 'rest'}
      variants={ICON_VARIANTS}
    >
      {/* cap that lifts on hover */}
      <motion.g
        variants={{ rest: { y: 0, rotate: 0 }, hover: { y: -3, rotate: -8 } }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: '12px 6px' }}
      >
        <rect x="9" y="3" width="6" height="3" rx="1" />
        <path d="M8 6h8l-1 4H9z" />
      </motion.g>
      {/* bottle stays */}
      <rect x="8" y="10" width="8" height="11" rx="2" />
      <motion.path
        d="M11 14h2"
        variants={{ rest: { opacity: 0.6 }, hover: { opacity: 1 } }}
      />
    </motion.svg>
  )
}

function ElectroniqueIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial="rest"
      animate={hover ? 'hover' : 'rest'}
      variants={ICON_VARIANTS}
    >
      <rect x="6" y="6" width="12" height="12" rx="2" />
      {/* pins flicker */}
      <motion.path
        d="M9 6V3M15 6V3M9 21v-3M15 21v-3M3 9h3M3 15h3M18 9h3M18 15h3"
        variants={{ rest: { opacity: 0.55 }, hover: { opacity: 1 } }}
        transition={{ duration: 0.4 }}
      />
      {/* core pulses */}
      <motion.rect
        x="9"
        y="9"
        width="6"
        height="6"
        rx="1"
        variants={{
          rest: { scale: 1, opacity: 0.6 },
          hover: { scale: [1, 1.15, 1], opacity: [0.6, 1, 0.8] },
        }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], repeat: hover ? Infinity : 0 }}
        style={{ transformOrigin: '12px 12px' }}
      />
    </motion.svg>
  )
}

function JardinIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial="rest"
      animate={hover ? 'hover' : 'rest'}
      variants={ICON_VARIANTS}
    >
      <path d="M12 21v-7" />
      {/* leaves sway */}
      <motion.path
        d="M12 14c-3 0-6-3-6-7 4 0 6 3 6 7z"
        variants={{ rest: { rotate: 0 }, hover: { rotate: [-8, 0] } }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: '12px 14px' }}
      />
      <motion.path
        d="M12 14c3 0 6-3 6-7-4 0-6 3-6 7z"
        variants={{ rest: { rotate: 0 }, hover: { rotate: [8, 0] } }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: '12px 14px' }}
      />
    </motion.svg>
  )
}

function MaisonIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial="rest"
      animate={hover ? 'hover' : 'rest'}
      variants={ICON_VARIANTS}
    >
      {/* roof drops in */}
      <motion.path
        d="M3 11l9-7 9 7"
        variants={{ rest: { y: 0 }, hover: { y: -1 } }}
        transition={{ duration: 0.4 }}
      />
      <path d="M5 10v10h14V10" />
      {/* door fills on hover */}
      <motion.path
        d="M10 20v-5h4v5"
        variants={{ rest: { opacity: 0.55 }, hover: { opacity: 1 } }}
        transition={{ duration: 0.4 }}
      />
      {/* lit window dot */}
      <motion.circle
        cx="12"
        cy="12.5"
        r="0.6"
        fill="currentColor"
        stroke="none"
        variants={{ rest: { opacity: 0 }, hover: { opacity: [0, 1, 0.5, 1] } }}
        transition={{ duration: 0.9, repeat: hover ? Infinity : 0 }}
      />
    </motion.svg>
  )
}

function ParfumerieIcon({ hover }: { hover: boolean }) {
  return (
    <motion.svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial="rest"
      animate={hover ? 'hover' : 'rest'}
      variants={ICON_VARIANTS}
    >
      <rect x="9" y="3" width="6" height="3" />
      <path d="M8 6h8v2H8z" />
      <rect x="7" y="8" width="10" height="13" rx="2" />
      {/* spray puffs */}
      <motion.g
        variants={{
          rest: { opacity: 0, x: 0 },
          hover: { opacity: [0, 1, 0], x: [0, 4, 6] },
        }}
        transition={{ duration: 1, ease: 'easeOut', repeat: hover ? Infinity : 0 }}
      >
        <path d="M17 10l3-1M17 12l3 0M17 14l3 1" />
      </motion.g>
    </motion.svg>
  )
}

const ICONS: Record<SectorId, (hover: boolean) => ReactNode> = {
  mode: (h) => <ModeIcon hover={h} />,
  cosmetique: (h) => <CosmetiqueIcon hover={h} />,
  electronique: (h) => <ElectroniqueIcon hover={h} />,
  'jardin-exterieur': (h) => <JardinIcon hover={h} />,
  'maison-decoration': (h) => <MaisonIcon hover={h} />,
  parfumerie: (h) => <ParfumerieIcon hover={h} />,
}

// ───────────── Main page ─────────────

export default function SectorsPage() {
  const { t } = useT()
  const locale = useLocale()

  const NUMBERS = [
    { value: t('sectorsPage.number1Value'), label: t('sectorsPage.number1Label') },
    { value: t('sectorsPage.number2Value'), label: t('sectorsPage.number2Label') },
    { value: t('sectorsPage.number3Value'), label: t('sectorsPage.number3Label') },
  ]

  return (
    <>
    <SeoHead
      title={t('seo.sectors.title')}
      description={t('seo.sectors.description')}
      path={localizedPath('sectors', locale)}
      jsonLd={[
        { '@context': 'https://schema.org', ...orgSchema(locale) },
        {
          '@context': 'https://schema.org',
          ...breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.sectors'), path: localizedPath('sectors', locale) },
          ]),
        },
      ]}
    />
    <main className="relative pt-28 sm:pt-32 pb-24 overflow-hidden isolate">
      {/* Background DA */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[1100px] -z-10 pointer-events-none">
        <div
          className="absolute inset-x-0 -top-20 h-[600px]"
          style={{
            background:
              'radial-gradient(ellipse at top, rgba(59,93,255,0.22), rgba(59,93,255,0.06) 45%, transparent 75%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 75%)',
          }}
        />
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -right-32 top-[25%] w-[420px] h-[420px] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)' }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-6">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xs sm:text-sm text-ink-500"
        >
          <Link to={localizedPath('home', locale)} className="hover:text-accent-700 transition-colors">
            {t('sectorsPage.breadcrumbHome')}
          </Link>
          <span className="mx-2 text-ink-300">/</span>
          <span className="text-ink-700">{t('sectorsPage.breadcrumbCurrent')}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{t('sectorsPage.badge')}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="mt-5 sm:mt-6 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(2.4rem,6vw,4.4rem)] leading-[1.04] max-w-4xl"
        >
          {t('sectorsPage.h1Lead')}{' '}
          <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
            {t('sectorsPage.h1Highlight')}
          </span>
          .
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-600 leading-relaxed max-w-3xl"
        >
          {t('sectorsPage.intro')}
        </motion.p>

        {/* Numbers strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 sm:mt-16 relative rounded-2xl sm:rounded-3xl border border-ink-100 bg-white/80 backdrop-blur-sm overflow-hidden"
        >
          <div className="relative grid grid-cols-3 divide-x divide-ink-100">
            {NUMBERS.map((n, i) => (
              <motion.div
                key={n.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="px-4 sm:px-8 py-6 sm:py-8"
              >
                <div className="font-semibold tracking-[-0.04em] leading-none text-[clamp(1.8rem,4vw,3rem)] bg-gradient-to-br from-ink-950 via-accent-700 to-accent-500 bg-clip-text text-transparent">
                  {n.value}
                </div>
                <div className="mt-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] font-semibold text-ink-500">
                  {n.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Sector cards grid */}
        <section className="mt-20 sm:mt-28">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
          >
            {t('sectorsPage.gridTitleLead')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {t('sectorsPage.gridTitleHighlight')}
            </span>
            .
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-4 text-ink-600 max-w-2xl leading-relaxed"
          >
            {t('sectorsPage.gridCaption')}
          </motion.p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {SECTOR_BASE.map((base, i) => (
              <SectorCard
                key={base.id}
                id={base.id}
                href={localizedPath('sectors.detail', locale, { slug: base.slug[locale] })}
                name={SECTOR_CONTENT[locale][base.id].name}
                desc={SECTOR_CONTENT[locale][base.id].heroIntro}
                cta={t('sectorsPage.cardCta')}
                delay={i * 0.07}
              />
            ))}
          </div>
        </section>

        {/* Why specialized 3PL — long-form SEO */}
        <section className="mt-20 sm:mt-28 grid lg:grid-cols-[280px_1fr] gap-10 lg:gap-16">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
              </span>
              <span className="font-medium">{t('sectorsPage.whyBadge')}</span>
            </span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.6rem,3.2vw,2.2rem)] leading-[1.1]"
            >
              {t('sectorsPage.whyTitleLead')}{' '}
              <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.05em] inline-block">
                {t('sectorsPage.whyTitleHighlight')}
              </span>
              .
            </motion.h2>
          </div>

          <div className="space-y-5 text-base sm:text-lg text-ink-700 leading-relaxed">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7 }}
            >
              {t('sectorsPage.whyPara1')}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              {t('sectorsPage.whyPara2')}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              {t('sectorsPage.whyPara3')}
            </motion.p>
          </div>
        </section>

        {/* ───────────── FINAL CTA — About-style dark dramatic ───────────── */}
        <section className="relative mt-20 sm:mt-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 text-white px-6 sm:px-12 py-16 sm:py-24 overflow-hidden isolate"
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-30 -z-10"
              style={{
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
                maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
              }}
            />
            <motion.div
              aria-hidden
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -right-32 -top-32 w-[460px] h-[460px] rounded-full blur-3xl -z-10"
              style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.55), transparent 70%)' }}
            />
            <motion.div
              aria-hidden
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
              className="absolute -left-32 -bottom-32 w-[380px] h-[380px] rounded-full blur-3xl -z-10"
              style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.35), transparent 70%)' }}
            />
            <motion.div
              aria-hidden
              animate={{ rotate: 360 }}
              transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
              className="absolute left-1/2 top-1/2 w-[600px] h-[600px] -translate-x-1/2 -translate-y-1/2 -z-10"
            >
              <span className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-accent-400/80 blur-[2px]" />
              <span className="absolute right-[12%] top-[18%] w-1.5 h-1.5 rounded-full bg-accent-300/70 blur-[2px]" />
            </motion.div>

            <div className="relative max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-accent-200">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-70 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
                </span>
                {t('sectorsPage.ctaBadge')}
              </span>

              <h3 className="mt-6 font-semibold tracking-[-0.035em] text-[clamp(2.4rem,6vw,4.4rem)] leading-[1.02]">
                {t('sectorsPage.ctaTitleLead')}{' '}
                <span className="font-semibold bg-gradient-to-r from-accent-300 via-accent-200 to-white bg-clip-text text-transparent pb-[0.08em] inline-block">
                  {t('sectorsPage.ctaTitleHighlight')}
                </span>
                <br className="hidden sm:block" />{t('sectorsPage.ctaTitleTail')}
              </h3>

              <p className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-300 leading-relaxed max-w-xl">
                {t('sectorsPage.ctaCopy')}
              </p>

              <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link
                  to={`${localizedPath('contact', locale)}#rendez-vous`}
                  className="relative group inline-flex items-center gap-2.5 bg-ink-100 hover:bg-accent-50 text-ink-950 rounded-full pl-6 pr-5 py-3.5 sm:py-4 text-sm sm:text-base font-semibold shadow-[0_24px_60px_-18px_rgba(59,93,255,0.7)] transition-colors overflow-hidden"
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-accent-200/60 to-transparent pointer-events-none"
                  />
                  <span className="relative">{t('sectorsPage.ctaPrimary')}</span>
                  <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-ink-950 text-white">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </Link>
                <Link
                  to={localizedPath('about', locale)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-5 py-3.5 sm:py-4 text-sm sm:text-base font-medium text-white/90 hover:bg-white/10 hover:border-white/30 transition-colors"
                >
                  {t('sectorsPage.ctaSecondary')}
                </Link>
              </div>

              <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs sm:text-sm text-ink-400">
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {t('sectorsPage.trust1')}
                </span>
                <span className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('sectorsPage.trust2')}
                </span>
                <span className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                    <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('sectorsPage.trust3')}
                </span>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </main>
    </>
  )
}

// ───────────── Sector card with hover-animated icon ─────────────

function SectorCard({
  id,
  href,
  name,
  desc,
  cta,
  delay,
}: {
  id: SectorId
  href: string
  name: string
  desc: string
  cta: string
  delay: number
}) {
  const [hover, setHover] = useState(false)
  const renderIcon = ICONS[id]

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      onHoverStart={() => setHover(true)}
      onHoverEnd={() => setHover(false)}
    >
      <Link
        to={href}
        className="group relative block h-full rounded-2xl border border-ink-100 bg-ink-100 p-6 hover:border-accent-200 hover:shadow-[0_24px_60px_-22px_rgba(59,93,255,0.4)] transition-all overflow-hidden"
      >
        {/* Corner halo */}
        <motion.div
          aria-hidden
          animate={{ opacity: hover ? 0.6 : 0 }}
          transition={{ duration: 0.5 }}
          className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-2xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.35), transparent 70%)' }}
        />

        <motion.div
          animate={{ scale: hover ? 1.06 : 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-accent-600 to-accent-700 text-white flex items-center justify-center shadow-[0_12px_28px_-12px_rgba(59,93,255,0.7)]"
        >
          {renderIcon ? renderIcon(hover) : null}
        </motion.div>

        <h3 className="relative mt-5 text-lg sm:text-xl font-semibold tracking-tight text-ink-950">
          {name}
        </h3>
        <p className="relative mt-1.5 text-sm text-ink-600 leading-relaxed line-clamp-3">
          {desc}
        </p>

        <div className="relative mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700 group-hover:text-accent-800 transition-colors">
          {cta}
          <motion.span
            aria-hidden
            animate={{ x: hover ? 4 : 0 }}
            transition={{ duration: 0.3 }}
            className="inline-block"
          >
            →
          </motion.span>
        </div>
      </Link>
    </motion.div>
  )
}
